import "./style.scss";

import ArrowBackIosIcon from "@mui/icons-material/ArrowBackIos";
import { TabContext, TabPanel } from "@mui/lab";
import { Avatar, Box, CircularProgress, Tab } from "@mui/material";
import Edit from "assets/img/svg/edit";
import Plus from "assets/img/svg/plus";
import ImportData from "components/ImportData";
import ModalBox from "components/Modal";
import ArticlesTable from "components/shared/ArticlesTable";
import BPSignedDocumentsTable from "components/shared/BPSignedDocumentsTable";
import { StyledTabs } from "components/shared/StyledTabs";
import ButtonMenu from "components/shared/ButtonMenu";
import EmptyState from "components/shared/EmptyState";
import ImagePopup from "components/shared/ImagePopup";
import InformationGrid from "components/shared/InformationGrid";
import LongTextBlock from "components/shared/LongTextBlock";
import StyledButton from "components/shared/StyledButton";
import Tags from "components/shared/Tags";
import { populateGridWithCustomFields } from "containers/CustomFieldsContainer/helpers";
import type { ITitle } from "containers/Titles/types";
import { PortalType } from "contexts/portal.context";
import { formatLocaleDateString, formatNumber } from "helpers/utils";
import { resolvePressChannelSlot } from "helpers/pressCalculationBase";
import { useCompanySettings } from "hooks/useCompanySettings";
import { useIsMobile } from "hooks/useIsMobile";
import usePageTitle from "hooks/usePageTitle";
import { usePermissions } from "hooks/usePermissions";
import { useCallback, useContext, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate, useParams } from "react-router-dom";
import { useLocation } from "react-router-dom";
import { getAllArticles, getAllArticlesBeneficiaryPortal } from "store/articleStore";
import { fetchAgreements } from "store/agreementsStore";
import { addArticles } from "store/articleStore";
import { toggleTitleDrawer } from "store/settingsStore";
import { useAppDispatch, useAppSelector } from "store/store";
import { deleteTag, fetchTitle, fetchTitleByBeneficiar } from "store/titleStore";
import type { IBeneficiariesCompanies } from "store/types";
import { ChannelCalculationMethod, ChannelType, EPortalType } from "store/types";

import variables from "../../assets/styles/_variables.module.scss";

export type TTags = { id: number; name: string; color: number };

const Publication = ({
    editMode = false,
    onEdit = () => {},
    beneficiaryCompanies,
}: {
    editMode?: boolean;
    onEdit?: () => void;
    beneficiaryCompanies?: IBeneficiariesCompanies[];
}) => {
    const [t] = useTranslation();
    const navigate = useNavigate();
    const dispatch = useAppDispatch();
    const location: any = useLocation();
    const portalType = useContext(PortalType);
    const { title, titleStatus, titles, beneficiarTitle, beneficiarTitleStatus, beneficiarTitles } = useAppSelector(
        (state) => state.titles,
    );

    const { hasPermissions } = usePermissions();

    // Fetch custom fields only of title resource:
    const { customFields } = useAppSelector((state) => state.customFields);
    const publicationCustomFields = customFields.filter((item) => item.relatedResource === "title");

    const { articles, articleExportStatus } = useAppSelector((state) => state.article);
    const { channels } = useAppSelector((state) => state.channels);
    const currentTitle = ["audiovisual", "press"].includes(portalType)
        ? title
        : portalType == "beneficiary"
          ? (beneficiarTitle as ITitle)
          : null;
    const titleLoadingStatus = ["audiovisual", "press"].includes(portalType)
        ? titleStatus
        : portalType == "beneficiary"
          ? beneficiarTitleStatus
          : null;
    const elementsLength = ["audiovisual", "press"].includes(portalType)
        ? titles
        : portalType == "beneficiary"
          ? beneficiarTitles
          : [];
    const [value, setValue] = useState(location.state != null && location.state.tab ? location.state.tab : "1");
    const [bpTab, setBpTab] = useState<"articles" | "signed-documents">("articles");
    const { id } = useParams();
    usePageTitle(t("Titles.Titles"));
    const [modalStatus, setModalStatus] = useState<string | null>(null);
    const [avatarDialogType, setAvatarDialogType] = useState(false);
    const { isMobile } = useIsMobile();

    const { getCompanySettings, companyInfos, isCompany } = useCompanySettings();
    const preferredIdentifier = getCompanySettings("preferred_title_identifier");
    const isPreferredIdentifierConcat = preferredIdentifier === "concat";

    const currentCompany = useMemo(() => {
        if (portalType === EPortalType.BENEFICIARY) {
            return beneficiaryCompanies?.find((item) => item.id === currentTitle?.companyId);
        } else {
            return companyInfos;
        }
    }, [portalType, beneficiaryCompanies, companyInfos, currentTitle]);

    const usesBothChannels = getCompanySettings("press_uses_multiple_channels");
    const preferredChannelId = getCompanySettings("press_preferred_channel");
    const preferredChannel = channels.find((channel) => channel.id === preferredChannelId);
    const isFolioBased = resolvePressChannelSlot(preferredChannel ?? { name: "", type: null }) === "folio";

    const folioPrice = useMemo(
        () =>
            currentTitle?.overriddenChannelsData?.find(
                (item) => item.calculationMethod == ChannelCalculationMethod.PACKAGE,
            )?.price ?? 0,
        [currentTitle],
    );

    /**
     * Publication infos formatted for the Information Grid.
     */
    const publicationInfos = useMemo(() => {
        if (!currentTitle) return {};
        const customFields = publicationCustomFields.filter((field) => field.valueType !== "longstring");

        const displayedFields: Record<string, string> = {};
        displayedFields[t("NewPublication.AnalysisCode")] = currentTitle?.analysisCode || "-";
        if (["press", "beneficiary"].includes(portalType))
            displayedFields[t("NewPublication.ParutionCode")] = currentTitle.otherInformation?.parutionCode || "-";
        displayedFields[t("NewPublication.DeliveryDate")] = currentTitle.otherInformation?.deliveryDate
            ? formatLocaleDateString({
                  date: currentTitle.otherInformation?.deliveryDate,
                  options: { numeric: true, timeZone: "UTC" },
              })
            : "-";
        displayedFields[t("NewPublication.ReleaseDate")] = currentTitle.releaseDate
            ? formatLocaleDateString({ date: currentTitle.releaseDate, options: { numeric: true, timeZone: "UTC" } })
            : "-";

        if (customFields && currentTitle?.customFields) {
            populateGridWithCustomFields({
                resourceData: currentTitle,
                resourceCustomFields: customFields,
                gridObject: displayedFields,
            });
        }

        return displayedFields;
    }, [publicationCustomFields, currentTitle]);

    const textAreaFields = useMemo(() => {
        const longStringFields = publicationCustomFields.filter((field) => field.valueType === "longstring");

        const displayedFields: { title: string; text: string }[] = [];

        if (longStringFields && currentTitle?.customFields) {
            longStringFields.forEach((item) => {
                const fieldData = currentTitle?.customFields?.find((field) => field.customFieldTypeId === item.id);
                displayedFields.push({ title: item.name, text: fieldData?.value ? fieldData?.value : "-" });
            });
        }

        return displayedFields;
    }, [publicationCustomFields, currentTitle]);

    const handleAddArticle = () =>
        navigate("/articles/new", { state: { publicationId: currentTitle?.id, publicationName: currentTitle?.name } });

    useEffect(() => {
        if (id) {
            if (["audiovisual", "press"].includes(portalType)) {
                dispatch(fetchTitle(id));
                dispatch(getAllArticles(+id));
                dispatch(fetchAgreements());
            } else if (portalType == "beneficiary") {
                dispatch(fetchTitleByBeneficiar(id));
                dispatch(getAllArticlesBeneficiaryPortal(+id));
            }
        }
    }, [dispatch, id]);

    const handleDeleteTag = useCallback(
        (i: number) => {
            dispatch(deleteTag({ titleId: +id!, tagId: i }));
        },
        [dispatch, id],
    );

    const routePublicationId = id ? Number(id) : null;
    const isShowingThisPublication = routePublicationId != null && currentTitle?.id === routePublicationId;
    if (titleLoadingStatus === "pending" && !isShowingThisPublication) {
        return (
            <Box
                sx={{
                    display: "flex",
                    width: "100%",
                    height: "100vh",
                    justifyContent: "center",
                    alignItems: "center",
                }}
            >
                <CircularProgress sx={{ color: variables["primary-purple"] }} />
            </Box>
        );
    }

    if (elementsLength.length < 1) {
        return (
            <Box
                sx={{
                    display: "flex",
                    width: "100%",
                    height: "100vh",
                    alignItems: "center",
                    justifyContent: "center",
                }}
            >
                <EmptyState type="publications" />
            </Box>
        );
    }

    return (
        <>
            {isMobile && (
                <>
                    <div className="title-bp-mobile-exit">
                        <div className="title-bp-mobile-exit-btn" onClick={() => dispatch(toggleTitleDrawer(false))}>
                            <ArrowBackIosIcon style={{ color: variables["foreground"] }} />
                            <span>{t("Titles.AllTitles")}</span>
                        </div>
                    </div>
                </>
            )}
            <div className="publication-container" style={editMode ? { overflow: "hidden" } : {}}>
                <ImagePopup
                    element={currentTitle}
                    avatarOpen={avatarDialogType}
                    setAvatarOpen={() => setAvatarDialogType(false)}
                />
                {currentTitle && (
                    <TabContext value={value}>
                        <div>
                            <div
                                className="publication-header"
                                style={
                                    portalType === EPortalType.BENEFICIARY && !isMobile
                                        ? { paddingInline: "1.5rem" }
                                        : undefined
                                }
                            >
                                <div className="publication-left-content">
                                    <Avatar
                                        onClick={() => currentTitle.avatarUrl !== "" && setAvatarDialogType(true)}
                                        src={currentTitle.avatarUrl}
                                        sx={{
                                            color: "black",
                                            backgroundColor: "#FFF2F1",
                                            // backgroundColor: background,
                                            width: "80px",
                                            height: "110px",
                                            borderRadius: "8px",
                                            cursor: currentTitle.avatarUrl !== "" ? "pointer" : "",
                                        }}
                                        variant="rounded"
                                        imgProps={{ sx: { objectFit: "cover" } }}
                                    >
                                        {(currentTitle?.name ?? "").slice(0, 1)}
                                    </Avatar>
                                    <div className="publication-header-base-data">
                                        <div className="tag-panel">
                                            <h2>{currentTitle?.name ?? ""}</h2>
                                        </div>
                                        <div className="divided-blocks">
                                            {currentTitle?.ean13 && (
                                                <div className="info">
                                                    {isPreferredIdentifierConcat ||
                                                    currentTitle.otherInformation?.issn ? (
                                                        <span>
                                                            {t("Publications.ISSN")} :{" "}
                                                            {currentTitle.otherInformation?.issn ?? "-"}
                                                        </span>
                                                    ) : (
                                                        <span>
                                                            {t("NewPublication.UniqueCode")} :{" "}
                                                            {currentTitle?.ean13 ?? "-"}
                                                        </span>
                                                    )}
                                                </div>
                                            )}
                                            {(folioPrice && isFolioBased) || (folioPrice && usesBothChannels) ? (
                                                <>
                                                    <div className="info">
                                                        <span>|</span>
                                                    </div>
                                                    <div className="info">
                                                        <span>
                                                            {`${formatNumber(folioPrice / 100, {
                                                                isCurrency: true,
                                                                always2Digit: true,
                                                            })} ${t("Publications.PerSlip")}`}
                                                        </span>
                                                    </div>
                                                </>
                                            ) : (
                                                ""
                                            )}
                                        </div>
                                        {["audiovisual", "press"].includes(portalType) && (
                                            <Tags
                                                elementId={currentTitle.id}
                                                elementType="titles"
                                                onDelete={handleDeleteTag}
                                                tags={currentTitle.tags}
                                            />
                                        )}
                                    </div>
                                </div>
                                {["audiovisual", "press"].includes(portalType) && (
                                    <div className="publication-header-buttons">
                                        <StyledButton
                                            sx={{ gap: "8px", padding: "8px 16px" }}
                                            onClick={() => onEdit()}
                                            buttonType="secondary"
                                        >
                                            <Edit />
                                            {t(`Publications.EditPublication_${portalType}`)}
                                        </StyledButton>
                                        <ButtonMenu
                                            mainActions={{
                                                icon: <Plus />,
                                                name: t(`Publications.AddArticle_${portalType}`),
                                                action: handleAddArticle,
                                            }}
                                            disabled={!hasPermissions(["article:new"])}
                                            tooltipText={
                                                !hasPermissions(["article:new"])
                                                    ? t("LogIn.NotAuthorizedException")
                                                    : ""
                                            }
                                            actions={[
                                                {
                                                    name: t(`Publications.ImportArticles_${portalType}`),
                                                    action: () => setModalStatus("import"),
                                                },
                                            ]}
                                        />
                                    </div>
                                )}
                            </div>
                            <div className="publication-tabs"></div>
                        </div>
                        {portalType === EPortalType.BENEFICIARY ? (
                            <TabContext value={bpTab}>
                                <StyledTabs
                                    value={bpTab}
                                    onChange={(_, newTab) => setBpTab(newTab)}
                                    sx={{ marginInline: "1.5rem" }}
                                >
                                    <Tab label={t("BPTitle.TabArticles")} value="articles" />
                                    <Tab label={t("BPTitle.TabSignedDocuments")} value="signed-documents" />
                                </StyledTabs>
                                <TabPanel value="articles" sx={{ padding: 0 }}>
                                    <ArticlesTable
                                        associatedArticles={articles}
                                        publication={currentTitle}
                                        articleExportStatus={articleExportStatus}
                                        pageType="publication"
                                        currentCompany={currentCompany}
                                    />
                                    <div className="bp-publication-informations">
                                        <InformationGrid data={publicationInfos} title={t("Titles.Informations")} />
                                        {textAreaFields.map((field) => (
                                            <LongTextBlock
                                                text={field.text}
                                                title={field.title}
                                                key={`title.longstring.${field.title.trim()}`}
                                            />
                                        ))}
                                    </div>
                                </TabPanel>
                                <TabPanel value="signed-documents" sx={{ padding: 0 }}>
                                    <div style={{ padding: "2rem 1.5rem" }}>
                                        <BPSignedDocumentsTable titleId={currentTitle.id} />
                                    </div>
                                </TabPanel>
                            </TabContext>
                        ) : (
                            <>
                                <ArticlesTable
                                    associatedArticles={articles}
                                    publication={currentTitle}
                                    articleExportStatus={articleExportStatus}
                                    pageType="publication"
                                    currentCompany={currentCompany}
                                />
                                <div className="bp-publication-informations">
                                    <InformationGrid data={publicationInfos} title={t("Titles.Informations")} />
                                    {textAreaFields.map((field) => (
                                        <LongTextBlock
                                            text={field.text}
                                            title={field.title}
                                            key={`title.longstring.${field.title.trim()}`}
                                        />
                                    ))}
                                </div>
                            </>
                        )}
                    </TabContext>
                )}

                <ModalBox
                    open={modalStatus === "import"}
                    dictionaryKey="articles"
                    onClose={() => setModalStatus(null)}
                    mainTitle={t(`ImportData.ImportArticles_${portalType}`)}
                >
                    <ImportData
                        dictionaryKey="articles"
                        close={() => setModalStatus(null)}
                        titleId={currentTitle?.id}
                        onSucceed={(payload) => dispatch(addArticles(payload.data))}
                    />
                </ModalBox>
            </div>
        </>
    );
};

export default Publication;
